import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Receipt, User, DollarSign, Calendar, FileText, ArrowRight, Save } from 'lucide-react';
import { useCreateInvoice } from '../hooks/useInvoices';

interface Merchant {
  id: string;
  name: string;
}

function fixArabicText(text: string | undefined | null): string {
  if (!text) return '';
  if (/[\u0600-\u06FF]/.test(text) && !text.includes("'D")) return text;
  try {
    const bytes = new Uint8Array(text.split('').map(c => c.charCodeAt(0)));
    return new TextDecoder('windows-1256').decode(bytes) || text;
  } catch (e) {
    return text;
  }
}

export function CreateInvoicePage() {
  const navigate = useNavigate();
  const createInvoiceMutation = useCreateInvoice();

  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [invoiceNumber, setInvoiceNumber] = useState(`INV-${Date.now().toString().slice(-6)}`);
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [paidAmount, setPaidAmount] = useState('0');
  const [paymentStatus, setPaymentStatus] = useState('Pending');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const loadMerchants = async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/Users');
        const allUsers = res.data.data?.items || res.data.data || [];
        
        const fetchedMerchants = allUsers
          .filter((u: any) => {
            const role = u.role?.toLowerCase();
            return role === 'merchant' || role === 'تاجر' || role === 'customer' || role === 'عميل';
          })
          .map((u: any) => ({
            id: String(u.id),
            name: fixArabicText(u.fullName || u.name)
          }));
        
        setMerchants(fetchedMerchants.length > 0 ? fetchedMerchants : []);
      } catch (err) {
        console.error("Error loading merchants for invoice:", err);
        setMerchants([]);
      } finally {
        setLoading(false);
      }
    };
    loadMerchants();
  }, []);

  const handleAmountChange = (total: string, paid: string) => {
    const t = Number(total) || 0;
    const p = Number(paid) || 0;
    
    if (t === 0) setPaymentStatus('Pending');
    else if (p >= t) setPaymentStatus('Paid');
    else if (p > 0 && p < t) setPaymentStatus('Partial');
    else setPaymentStatus('Pending');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMerchantId) {
      alert('الرجاء اختيار التاجر');
      return;
    }

    try {
      setSubmitting(true);
      const merchant = merchants.find(m => m.id === selectedMerchantId);
      
      const payload = {
        customerId: selectedMerchantId || undefined,
        dueDate: new Date(invoiceDate).toISOString(),
        notes,
        items: [
          {
            description: notes || 'فاتورة عامة',
            quantity: 1,
            unitPrice: Number(totalAmount) || 0,
            totalPrice: Number(totalAmount) || 0
          }
        ]
      };

      await createInvoiceMutation.mutateAsync(payload);
      alert('تم إنشاء الفاتورة بنجاح!');
      navigate('/invoices');
    } catch (error: any) {
      console.error("Invoice creation error:", error);
      alert(error.response?.data?.message || 'حدث خطأ أثناء إنشاء الفاتورة');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-gray-500">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-3"></div>
        جاري تحميل بيانات التجار...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto text-right" dir="rtl">
      <div className="flex items-center justify-between mb-6 border-b pb-4">
        <div className="flex items-center gap-2">
          <Receipt className="w-6 h-6 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">إنشاء فاتورة جديدة</h1>
            <p className="text-xs text-gray-500 mt-0.5">تسجيل فاتورة بيع أو خدمة للتاجر</p>
          </div>
        </div>
        <button 
          type="button" 
          onClick={() => navigate('/invoices')} 
          className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1 bg-gray-100 px-3 py-2 rounded-xl"
        >
          <ArrowRight className="w-4 h-4" /> العودة للقائمة
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
              <User className="w-3.5 h-3.5 text-orange-500" /> التاجر / العميل
            </label>
            <select
              required
              value={selectedMerchantId}
              onChange={(e) => setSelectedMerchantId(e.target.value)}
              className="w-full p-2.5 border rounded-xl bg-white text-xs font-semibold text-gray-700 outline-none focus:border-orange-500"
            >
              <option value="">-- اختر التاجر --</option>
              {merchants.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
              <FileText className="w-3.5 h-3.5 text-orange-500" /> رقم الفاتورة
            </label>
            <input 
              type="text" 
              required
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className="w-full p-2.5 border rounded-xl text-xs outline-none focus:border-orange-500" 
            />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
              <DollarSign className="w-3.5 h-3.5 text-orange-500" /> المبلغ الإجمالي
            </label>
            <input 
              type="number" 
              required
              value={totalAmount}
              onChange={(e) => {
                setTotalAmount(e.target.value);
                handleAmountChange(e.target.value, paidAmount);
              }}
              className="w-full p-2.5 border rounded-xl text-xs outline-none focus:border-orange-500" 
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
              <DollarSign className="w-3.5 h-3.5 text-orange-500" /> المبلغ المدفوع
            </label>
            <input 
              type="number" 
              required
              value={paidAmount}
              onChange={(e) => {
                setPaidAmount(e.target.value);
                handleAmountChange(totalAmount, e.target.value);
              }}
              className="w-full p-2.5 border rounded-xl text-xs outline-none focus:border-orange-500" 
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">حالة الدفع</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="w-full p-2.5 border rounded-xl bg-white text-xs font-semibold text-gray-700 outline-none focus:border-orange-500"
            >
              <option value="Pending">غير مدفوع</option>
              <option value="Partial">مدفوع جزئياً</option>
              <option value="Paid">مدفوع بالكامل</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
              <Calendar className="w-3.5 h-3.5 text-orange-500" /> تاريخ الفاتورة
            </label>
            <input 
              type="date" 
              required
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="w-full p-2.5 border rounded-xl text-xs outline-none focus:border-orange-500 font-mono" 
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">ملاحظات</label>
          <textarea 
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full p-2.5 border rounded-xl text-xs outline-none focus:border-orange-500 resize-none" 
            placeholder="أي تفاصيل إضافية حول الفاتورة..."
          />
        </div>

        <div className="flex justify-end pt-2">
          <button 
            type="submit" 
            disabled={submitting}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl text-xs flex items-center gap-1.5 shadow-md shadow-orange-500/20 transition-all"
          >
            <Save className="w-4 h-4" /> {submitting ? 'جاري الحفظ...' : 'حفظ الفاتورة'}
          </button>
        </div>
      </form>
    </div>
  );
}
